/* =========================================================
   LOMSIR Impact Homepage System
   File: assets/lomsir-impact.js

   Purpose:
   - Lightweight homepage interactions
   - FAQ accordion
   - Ingredient console tabs
   - Product rail controls
   - Scroll reveal animation
   - Mobile-friendly UX
   ========================================================= */

(function () {
  'use strict';

  var LOMSIR_IMPACT = {
    selectors: {
      faqItem: '.lomsir-impact-faq-item',
      faqQuestion: '.lomsir-impact-faq-question',
      faqAnswer: '.lomsir-impact-faq-answer',

      console: '[data-lomsir-impact-console]',
      consoleTab: '[data-lomsir-impact-tab]',
      consolePanel: '[data-lomsir-impact-panel]',

      reveal: '[data-lomsir-impact-reveal]',

      productRail: '[data-lomsir-impact-rail]',
      railPrev: '[data-lomsir-impact-rail-prev]',
      railNext: '[data-lomsir-impact-rail-next]',

      goalCard: '.lomsir-impact-goal-card',
      announcement: '.lomsir-impact-announcement'
    },

    classes: {
      open: 'is-open',
      active: 'is-active',
      visible: 'is-visible',
      selected: 'is-selected'
    },

    init: function (root) {
      var scope = root || document;

      this.initFaq(scope);
      this.initIngredientConsole(scope);
      this.initReveal(scope);
      this.initProductRail(scope);
      this.initGoalCards(scope);
      this.initAnnouncement(scope);
    },

    /* ------------------------------
       1. FAQ Accordion
       ------------------------------ */

    initFaq: function (scope) {
      var questions = scope.querySelectorAll(this.selectors.faqQuestion);

      if (!questions.length) return;

      questions.forEach(function (question) {
        if (question.dataset.lomsirImpactBound === 'true') return;

        question.dataset.lomsirImpactBound = 'true';

        question.addEventListener('click', function () {
          var item = question.closest(LOMSIR_IMPACT.selectors.faqItem);
          if (!item) return;

          var answer = item.querySelector(LOMSIR_IMPACT.selectors.faqAnswer);
          var isOpen = item.classList.contains(LOMSIR_IMPACT.classes.open);

          item.classList.toggle(LOMSIR_IMPACT.classes.open, !isOpen);
          question.setAttribute('aria-expanded', String(!isOpen));

          if (answer) {
            answer.hidden = isOpen;
          }
        });
      });
    },

    /* ------------------------------
       2. Ingredient Console Tabs
       ------------------------------ */

    initIngredientConsole: function (scope) {
      var consoles = scope.querySelectorAll(this.selectors.console);

      if (!consoles.length) return;

      consoles.forEach(function (consoleEl) {
        if (consoleEl.dataset.lomsirImpactConsoleBound === 'true') return;

        consoleEl.dataset.lomsirImpactConsoleBound = 'true';

        var tabs = consoleEl.querySelectorAll(LOMSIR_IMPACT.selectors.consoleTab);
        var panels = consoleEl.querySelectorAll(LOMSIR_IMPACT.selectors.consolePanel);

        if (!tabs.length || !panels.length) return;

        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            var target = tab.getAttribute('data-lomsir-impact-tab');

            if (!target) return;

            tabs.forEach(function (item) {
              var isActive = item === tab;

              item.classList.toggle(LOMSIR_IMPACT.classes.active, isActive);
              item.setAttribute('aria-selected', String(isActive));
            });

            panels.forEach(function (panel) {
              var isTarget = panel.getAttribute('data-lomsir-impact-panel') === target;

              panel.classList.toggle(LOMSIR_IMPACT.classes.active, isTarget);
              panel.hidden = !isTarget;
            });
          });
        });

        var hasActiveTab = consoleEl.querySelector(
          LOMSIR_IMPACT.selectors.consoleTab + '.' + LOMSIR_IMPACT.classes.active
        );

        if (!hasActiveTab && tabs[0]) {
          tabs[0].click();
        }
      });
    },

    /* ------------------------------
       3. Scroll Reveal Animation
       ------------------------------ */

    initReveal: function (scope) {
      var revealItems = scope.querySelectorAll(this.selectors.reveal);

      if (!revealItems.length) return;

      if (!('IntersectionObserver' in window)) {
        revealItems.forEach(function (item) {
          item.classList.add(LOMSIR_IMPACT.classes.visible);
        });

        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(LOMSIR_IMPACT.classes.visible);
            observer.unobserve(entry.target);
          });
        },
        {
          root: null,
          threshold: 0.12,
          rootMargin: '0px 0px -40px 0px'
        }
      );

      revealItems.forEach(function (item) {
        if (item.dataset.lomsirImpactRevealBound === 'true') return;

        item.dataset.lomsirImpactRevealBound = 'true';
        observer.observe(item);
      });
    },

    /* ------------------------------
       4. Product Rail Controls
       ------------------------------ */

    initProductRail: function (scope) {
      var rails = scope.querySelectorAll(this.selectors.productRail);

      if (!rails.length) return;

      rails.forEach(function (rail) {
        if (rail.dataset.lomsirImpactRailBound === 'true') return;

        rail.dataset.lomsirImpactRailBound = 'true';

        var section = rail.closest('.lomsir-impact-section');
        if (!section) return;

        var prevButton = section.querySelector(LOMSIR_IMPACT.selectors.railPrev);
        var nextButton = section.querySelector(LOMSIR_IMPACT.selectors.railNext);

        var getStep = function () {
          var firstCard = rail.querySelector('.lomsir-impact-product-card');

          if (!firstCard) {
            return Math.round(rail.clientWidth * 0.8);
          }

          var cardWidth = firstCard.getBoundingClientRect().width;
          return Math.round(cardWidth + 18);
        };

        if (prevButton) {
          prevButton.addEventListener('click', function () {
            rail.scrollBy({
              left: -getStep(),
              behavior: 'smooth'
            });
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', function () {
            rail.scrollBy({
              left: getStep(),
              behavior: 'smooth'
            });
          });
        }

        var isDown = false;
        var startX = 0;
        var scrollLeft = 0;

        rail.addEventListener('pointerdown', function (event) {
          if (window.innerWidth >= 990) return;

          isDown = true;
          startX = event.pageX - rail.offsetLeft;
          scrollLeft = rail.scrollLeft;
          rail.setPointerCapture(event.pointerId);
        });

        rail.addEventListener('pointermove', function (event) {
          if (!isDown) return;

          var x = event.pageX - rail.offsetLeft;
          var walk = (x - startX) * 1.1;
          rail.scrollLeft = scrollLeft - walk;
        });

        rail.addEventListener('pointerup', function () {
          isDown = false;
        });

        rail.addEventListener('pointercancel', function () {
          isDown = false;
        });
      });
    },

    /* ------------------------------
       5. Goal Card Active Feedback
       ------------------------------ */

    initGoalCards: function (scope) {
      var cards = scope.querySelectorAll(this.selectors.goalCard);

      if (!cards.length) return;

      cards.forEach(function (card) {
        if (card.dataset.lomsirImpactGoalBound === 'true') return;

        card.dataset.lomsirImpactGoalBound = 'true';

        card.addEventListener('pointerdown', function () {
          card.classList.add(LOMSIR_IMPACT.classes.selected);
        });

        card.addEventListener('pointerup', function () {
          window.setTimeout(function () {
            card.classList.remove(LOMSIR_IMPACT.classes.selected);
          }, 160);
        });

        card.addEventListener('pointerleave', function () {
          card.classList.remove(LOMSIR_IMPACT.classes.selected);
        });
      });
    },

    /* ------------------------------
       6. Announcement Bar Safety
       ------------------------------ */

    initAnnouncement: function (scope) {
      var announcements = scope.querySelectorAll(this.selectors.announcement);

      if (!announcements.length) return;

      announcements.forEach(function (bar) {
        if (bar.dataset.lomsirImpactAnnouncementBound === 'true') return;

        bar.dataset.lomsirImpactAnnouncementBound = 'true';

        bar.addEventListener('mouseenter', function () {
          bar.setAttribute('data-paused', 'true');
        });

        bar.addEventListener('mouseleave', function () {
          bar.removeAttribute('data-paused');
        });
      });
    },

    /* ------------------------------
       7. Shopify Theme Editor Support
       ------------------------------ */

    initThemeEditor: function () {
      document.addEventListener('shopify:section:load', function (event) {
        if (!event || !event.target) return;

        LOMSIR_IMPACT.init(event.target);
      });

      document.addEventListener('shopify:block:select', function (event) {
        if (!event || !event.target) return;

        var faqItem = event.target.closest(LOMSIR_IMPACT.selectors.faqItem);

        if (faqItem) {
          var question = faqItem.querySelector(LOMSIR_IMPACT.selectors.faqQuestion);
          var answer = faqItem.querySelector(LOMSIR_IMPACT.selectors.faqAnswer);

          faqItem.classList.add(LOMSIR_IMPACT.classes.open);

          if (question) {
            question.setAttribute('aria-expanded', 'true');
          }

          if (answer) {
            answer.hidden = false;
          }
        }
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      LOMSIR_IMPACT.init(document);
      LOMSIR_IMPACT.initThemeEditor();
    });
  } else {
    LOMSIR_IMPACT.init(document);
    LOMSIR_IMPACT.initThemeEditor();
  }
})();